import { useQuery } from '@tanstack/react-query'

import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Button } from '../components/ui/button'
import { Blank, Panel, Row } from '../components/ui/panel'
import { Table, TBody, Td, Th, THead, Tr } from '../components/ui/table'
import { Chip } from '../components/ui/tone'
import { api, type Page, type WebhookDelivery, type WebhookSourceCount } from '../lib/api'
import { useSession } from '../lib/session'

/**
 * Inbound webhook deliveries — architecture.md §50, §51.
 *
 * **These are webhooks arriving, not webhooks being sent.** §50's gateway terminates traffic from
 * providers and from recipients; Mail Room offers no outbound webhook subscriptions, and this
 * screen said it did until [P035](../../docs/problems/p035-the-console-cites-the-wrong-sections.md).
 *
 * ### Why it took until 2026-08-22 to wire
 *
 * `GET /webhooks` has existed since 2026-08-20 and `status.md` recorded this screen as blocked on
 * "the console has no platform-plane surface yet" — which was the true answer. `webhook_events` has
 * **no `organizationId`**: a delivery arrives before anything is known about it, and attribution is
 * the result of processing rather than a precondition for storing it. A tenant-scoped screen could
 * therefore never show the deliveries worth looking at, which are exactly the ones nothing could
 * attribute.
 *
 * So this is platform plane, like [Platform](platform.tsx). No organization role reaches it,
 * including SUPER_ADMIN: one tenant's failed provider callback is not another tenant's business.
 *
 * ### The body is never shown, and the API never sends it
 *
 * §59's data minimisation applies to a debugging screen exactly as it applies to a reply. What
 * comes back is the *shape* of a delivery — where it came from, when, whether it was processed,
 * and §51's dedup hash, which answers "have we seen this exact body before" without the body.
 */

/** §50's gateway paths, as the API reports them. Anything unrecognised keeps its raw name. */
const SOURCE_LABEL: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  zoho: 'Zoho',
  unsubscribe: 'Unsubscribe',
  tracking: 'Tracking',
}

function sourceLabel(source: string): string {
  return SOURCE_LABEL[source] ?? source
}

/** Processed is fine, received is in flight, anything else has stalled or failed. */
function statusTone(status: string): 'ok' | 'warn' | 'bad' | 'neutral' {
  if (status === 'processed') return 'ok'
  if (status === 'received') return 'warn'
  if (status === 'failed' || status === 'rejected') return 'bad'
  return 'neutral'
}

export function WebhooksScreen() {
  const { isPlatformStaff } = useSession()

  const deliveries = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => api.get<Page<WebhookDelivery>>('/webhooks?take=50'),
    enabled: isPlatformStaff,
  })

  const sources = useQuery({
    queryKey: ['webhooks', 'sources'],
    queryFn: () => api.get<WebhookSourceCount[]>('/webhooks/sources'),
    enabled: isPlatformStaff,
  })

  if (!isPlatformStaff) {
    return (
      <Screen>
        <ScreenHeader
          title="Webhooks"
          description="Deliveries arriving at §50's gateway, from providers and from recipients."
        />
        <Panel>
          <Blank
            icon="denied"
            title="Deliveries belong to the platform plane"
            body="A webhook arrives before anything is known about which organization it concerns, so these are Axmion's rather than any tenant's — no organization role reaches them, including SUPER_ADMIN."
          />
        </Panel>
      </Screen>
    )
  }

  const rows = deliveries.data?.items ?? []

  return (
    <Screen>
      <ScreenHeader
        title="Webhooks"
        description="Deliveries arriving at §50's gateway. These are webhooks Mail Room receives — it offers no outbound subscriptions."
      />
      <Panel
        title="Inbound deliveries"
        description="Newest first. The body is never stored on this screen or returned by the API (§59) — the hash is what answers a retry."
        actions={
          <Button
            size="sm"
            onClick={() => void deliveries.refetch()}
            disabled={deliveries.isFetching}
          >
            {deliveries.isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      >
        {(sources.data ?? []).length > 0 && (
          <Row label="By source">
            <span className="text-muted">
              {(sources.data ?? [])
                .map((row) => `${sourceLabel(row.source)} ${row.status}: ${row.count}`)
                .join(' · ')}
            </span>
          </Row>
        )}

        {deliveries.isError ? (
          <Blank
            icon="error"
            tone="bad"
            title="Deliveries could not be read"
            action={<Button onClick={() => void deliveries.refetch()}>Try again</Button>}
          />
        ) : rows.length > 0 ? (
          <Table>
            <THead>
              <Th width={170}>Received</Th>
              <Th width={130}>Source</Th>
              <Th width={120}>Status</Th>
              <Th width={170}>Processed</Th>
              <Th>Body hash (§51)</Th>
            </THead>
            <TBody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  <Td className="text-muted">{new Date(row.receivedAt).toLocaleString()}</Td>
                  <Td>{sourceLabel(row.source)}</Td>
                  <Td>
                    <Chip tone={statusTone(row.status)}>{row.status}</Chip>
                  </Td>
                  <Td className="text-muted">
                    {row.processedAt ? (
                      new Date(row.processedAt).toLocaleString()
                    ) : (
                      <span className="text-faint">not yet</span>
                    )}
                  </Td>
                  <Td className="text-faint font-mono text-[11px]">
                    {row.payloadHash ? row.payloadHash.slice(0, 24) : '—'}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        ) : (
          <Blank
            icon="empty"
            title="Nothing has arrived yet"
            body="No provider points at §50's gateway, and Mail Room reads no mailbox (P038). The first deliveries will be bounce and complaint notifications, once a return path exists on a domain Axmion owns."
          />
        )}
      </Panel>
    </Screen>
  )
}
